import { useCallback, useEffect, useState } from 'react';
import { CreditCard, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminWalletService, getApiErrorMessage } from '@/services';
import type { WalletProviderSettings } from '@/types/wallet';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { notify } from '@/utils/notify';

const IDENTITY_OPTIONS = [
  { value: 'personal_nin', label: 'NIN (Personal)' },
  { value: 'personal', label: 'BVN (Personal)' },
  { value: 'company', label: 'CAC (Company)' },
] as const;

export function AdminVirtualAccountsPage() {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [enabled, setEnabled] = useState(true);
  const [identityType, setIdentityType] = useState<WalletProviderSettings['identityType']>('personal_nin');
  const [identityLicense, setIdentityLicense] = useState('');
  const [chargePercent, setChargePercent] = useState('1');
  const [chargeCap, setChargeCap] = useState('50');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminWalletService.getProviderSettings('palmpay');
      const next = res.data?.settings ?? null;
      if (next) {
        setEnabled(next.enabled);
        setIdentityType(next.identityType);
        setIdentityLicense(next.identityLicense ?? '');
        setChargePercent(String(next.chargePercent));
        setChargeCap(String(next.chargeCap));
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load wallet settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await adminWalletService.updateProviderSettings('palmpay', {
        enabled,
        identityType,
        identityLicense: identityLicense.trim() || null,
        chargePercent: Number(chargePercent),
        chargeCap: Number(chargeCap),
      });
      notify.success('Virtual account settings saved');
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  }

  const inputClass = cn(
    'w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2',
    shell.input,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader label="Loading settings" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold sm:text-2xl">Virtual Account Setup</h1>
        <p className={cn('text-sm', shell.cardMuted)}>
          PalmPay identity and funding fee settings used when generating user virtual accounts
        </p>
      </div>

      <Card className={cn('p-4 sm:p-6', shell.card)}>
        <div className="mb-4 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          <h2 className="text-base font-semibold">PalmPay provider</h2>
        </div>

        <form onSubmit={(e) => void handleSave(e)} className="space-y-5">
          <label className="flex items-center gap-3 text-sm font-medium">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            Enable PalmPay wallet funding
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="identity-type" className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                Identity type for VA generation
              </label>
              <select
                id="identity-type"
                value={identityType}
                onChange={(e) => setIdentityType(e.target.value as WalletProviderSettings['identityType'])}
                className={inputClass}
              >
                {IDENTITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="identity-license" className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                Shared license number (NIN / BVN / CAC)
              </label>
              <input
                id="identity-license"
                type="text"
                value={identityLicense}
                onChange={(e) => setIdentityLicense(e.target.value)}
                placeholder="11-digit NIN/BVN or RC/BN number"
                className={inputClass}
              />
              <p className={cn('mt-1 text-xs', shell.cardMuted)}>
                Used when a user has no NIN/BVN on profile. Per-user profile values take priority.
              </p>
            </div>

            <div>
              <label htmlFor="charge-percent" className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                Funding fee (%)
              </label>
              <input
                id="charge-percent"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={chargePercent}
                onChange={(e) => setChargePercent(e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="charge-cap" className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                Fee cap (₦)
              </label>
              <input
                id="charge-cap"
                type="number"
                min="0"
                step="0.01"
                value={chargeCap}
                onChange={(e) => setChargeCap(e.target.value)}
                className={inputClass}
              />
              <p className={cn('mt-1 text-xs', shell.cardMuted)}>
                Example: 1% on ₦100 = ₦1 fee. Cap stops fee above this amount.
              </p>
            </div>
          </div>

          <Button type="submit" loading={saving} loadingText="Saving...">
            <Save className="h-4 w-4" />
            Save settings
          </Button>
        </form>
      </Card>
    </div>
  );
}
